import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Wishlist — persisted in localStorage. Holds an array of compact product
 * snapshots (id, name, image, price, currency, slug, preorder) so the
 * /wishlist page can render without round-tripping to the API.
 */
const STORAGE_KEY = 'tt:wishlist:v1';

const Ctx = createContext(null);

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeStore = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
};

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState(() => readStore());

  // Keep multiple tabs in sync
  useEffect(() => {
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setItems(readStore()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => { writeStore(items); }, [items]);

  const has = useCallback((id) => items.some((i) => i.id === id), [items]);

  const toggle = useCallback((product) => {
    setItems((cur) => {
      if (cur.some((i) => i.id === product.id)) {
        return cur.filter((i) => i.id !== product.id);
      }
      const snap = {
        id: product.id,
        name: product.name,
        slug: product.handle || product.slug || product.id,
        image: product.images?.[0] || product.media?.primary_image || null,
        price: product.price,
        currency: product.currency || 'AUD',
        preorder: !!product.preorder,
        added_at: new Date().toISOString(),
      };
      return [snap, ...cur];
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((cur) => cur.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ items, has, toggle, remove, clear, count: items.length }), [items, has, toggle, remove, clear]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useWishlist must be inside <WishlistProvider>');
  return ctx;
};
